import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Grid,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  HStack,
  VStack,
  Divider,
  Progress,
  Icon,
  Avatar,
  Tag,
  useColorModeValue,
  CircularProgress,
  CircularProgressLabel,
  Spinner,
  Tooltip
} from '@chakra-ui/react';
import {
  FiArrowUp,
  FiArrowDown,
  FiBarChart2,
  FiActivity,
  FiStar,
  FiZap,
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiSearch,
  FiCheck,
  FiAlertCircle,
  FiTarget,
  FiInfo,
  FiMessageCircle,
  FiClock
} from 'react-icons/fi';
import './DesolAI.css';

const DesolAI = () => {
  const [trendingTokens, setTrendingTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  // Theme colors
  const cardBg = useColorModeValue('white', 'dark.card');
  const borderColor = useColorModeValue('gray.200', 'dark.border');
  const textColor = useColorModeValue('gray.800', 'dark.textPrimary');
  const secondaryTextColor = useColorModeValue('gray.600', 'dark.textSecondary');
  const statBg = useColorModeValue('blue.50', 'rgba(118, 120, 237, 0.1)');

  useEffect(() => {
    // This would be replaced with an actual API call
    const fetchTrendingTokens = async () => {
      try {
        // Enhanced simulated API response
        const data = [
          {
            symbol: 'BONK',
            name: 'Bonk',
            price: '$0.00002',
            change24h: '+15.2%',
            volume24h: '$2.5M',
            marketCap: '$120M',
            aiScore: 85,
            logo: 'https://cryptologos.cc/logos/bonk-bonk-logo.png',
            shortDescription: 'Popular Solana-based meme token'
          },
          {
            symbol: 'TRUMP',
            name: 'Trump Coin',
            price: '$0.05',
            change24h: '+22.7%',
            volume24h: '$1.8M',
            marketCap: '$50M',
            aiScore: 78,
            logo: 'https://cryptologos.cc/logos/placeholder-logo.png',
            shortDescription: 'Political meme token with high volatility'
          },
          {
            symbol: 'BERN',
            name: 'Bernie Coin',
            price: '$0.02',
            change24h: '-5.3%',
            volume24h: '$950K',
            marketCap: '$30M',
            aiScore: 62,
            logo: 'https://cryptologos.cc/logos/placeholder-logo.png',
            shortDescription: 'Political token with decreased momentum'
          },
          {
            symbol: 'DESOL',
            name: 'Desol Token',
            price: '$0.004',
            change24h: '+8.1%',
            volume24h: '$750K',
            marketCap: '$20M',
            aiScore: 73,
            logo: 'https://cryptologos.cc/logos/placeholder-logo.png',
            shortDescription: 'Native token of the Desol AI platform'
          },
          {
            symbol: 'WOOF',
            name: 'Woof Coin',
            price: '$0.0003',
            change24h: '+35.6%',
            volume24h: '$3.2M',
            marketCap: '$15M',
            aiScore: 91,
            logo: 'https://cryptologos.cc/logos/placeholder-logo.png',
            shortDescription: 'New dog-themed token with high momentum'
          },
          {
            symbol: 'PEPE',
            name: 'Pepe',
            price: '$0.0000095',
            change24h: '+3.2%',
            volume24h: '$12.5M',
            marketCap: '$400M',
            aiScore: 69,
            logo: 'https://cryptologos.cc/logos/pepe-pepe-logo.png',
            shortDescription: 'Popular frog-themed meme token'
          }
        ];
        
        setTrendingTokens(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching trending tokens:', error);
        setIsLoading(false);
      }
    };

    fetchTrendingTokens();
  }, []);

  const handleTokenSelect = (token) => {
    setSelectedToken(token);
    setIsAnalyzing(true);
    
    // Simulate AI analysis
    setTimeout(() => {
      setAnalysis({
        sentiment: {
          score: 78,
          sources: {
            twitter: 82,
            reddit: 75,
            telegram: 76
          },
          keywords: ['moon', 'pump', 'bullish', 'airdrop', 'community']
        },
        technical: {
          buySignals: 7,
          sellSignals: 3,
          prediction: 'Bullish',
          support: token.price,
          resistance: `$${(parseFloat(token.price.replace('$', '')) * 1.25).toFixed(6)}`
        },
        recommendation: {
          action: 'BUY',
          confidence: 'High',
          targetPrice: `$${(parseFloat(token.price.replace('$', '')) * 1.3).toFixed(6)}`,
          stopLoss: `$${(parseFloat(token.price.replace('$', '')) * 0.85).toFixed(6)}`,
          timeframe: '24-48 hours',
          reasoning: `${token.symbol} is showing strong social sentiment and increasing volume. Recent wallet analysis shows accumulation by larger holders. Technical indicators suggest a potential breakout in the next 24-48 hours.`
        },
        riskAnalysis: {
          riskScore: 65,
          volatility: 'High',
          liquidityRisk: 'Medium',
          marketCycle: 'Early Growth',
          whaleConcentration: 'Moderate'
        },
        historicalPerformance: [
          { period: '1d', value: token.change24h },
          { period: '7d', value: token.change24h.startsWith('+') ? `+${(parseFloat(token.change24h.replace('+', '').replace('%', '')) * 1.3).toFixed(1)}%` : `-${(parseFloat(token.change24h.replace('-', '').replace('%', '')) * 0.7).toFixed(1)}%` },
          { period: '30d', value: token.change24h.startsWith('+') ? `+${(parseFloat(token.change24h.replace('+', '').replace('%', '')) * 2.5).toFixed(1)}%` : `-${(parseFloat(token.change24h.replace('-', '').replace('%', '')) * 1.2).toFixed(1)}%` }
        ]
      });
      setIsAnalyzing(false);
    }, 2000);
  };

  const handlePromoteToGovernance = () => {
    alert('Trade proposal has been submitted to governance for voting!');
    // In a real app, this would make an API call to submit the proposal using Solana program
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="50vh" direction="column">
        <Spinner size="xl" color="brand.400" thickness="4px" speed="0.65s" mb={4} />
        <Text color={secondaryTextColor}>Loading AI analysis data...</Text>
      </Flex>
    );
  }

  return (
    <Container maxW="container.xl" py={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading as="h1" size="xl" color={textColor}>Desol AI</Heading>
          <Text color={secondaryTextColor} mt={1}>AI-powered trading analysis and recommendations</Text>
        </Box>
        <Box bg={statBg} px={6} py={3} borderRadius="lg" textAlign="right">
          <Text fontWeight="bold" fontSize="xl" color="brand.400">
            <Icon as={FiActivity} mr={2} />
            {trendingTokens.length} Tokens Analyzed
          </Text>
          <Text fontSize="sm" color={secondaryTextColor}>
            Updated every 5 minutes
          </Text>
        </Box>
      </Flex>
      
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <VStack align="stretch" spacing={4}>
          <Box 
            bg={cardBg} 
            p={4} 
            borderRadius="lg" 
            borderWidth="1px" 
            borderColor={borderColor}
          >
            <Heading size="md" mb={3} color={textColor}>
              <Flex align="center">
                <Icon as={FiTrendingUp} mr={2} />
                Trending Tokens
              </Flex>
            </Heading>
            
            <VStack spacing={3} align="stretch">
              {trendingTokens.map((token, index) => (
                <Card 
                  key={index}
                  variant="outline"
                  bg={selectedToken?.symbol === token.symbol ? statBg : cardBg}
                  cursor="pointer"
                  borderColor={selectedToken?.symbol === token.symbol ? 'brand.400' : borderColor}
                  _hover={{ borderColor: 'brand.400', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                  onClick={() => handleTokenSelect(token)}
                >
                  <CardBody>
                    <Flex justify="space-between" align="center">
                      <HStack spacing={3}>
                        <Avatar size="sm" name={token.name} src={token.logo} bg="gray.200" />
                        <Box>
                          <Flex align="center">
                            <Heading size="sm" color={textColor}>{token.symbol}</Heading>
                            <Text ml={2} fontSize="xs" color={secondaryTextColor}>{token.name}</Text>
                          </Flex>
                          <Text fontSize="xs" color={secondaryTextColor} noOfLines={1}>
                            {token.shortDescription}
                          </Text>
                        </Box>
                      </HStack>
                      
                      <Flex direction="column" align="flex-end">
                        <CircularProgress value={token.aiScore} color="brand.400" size="40px">
                          <CircularProgressLabel fontSize="xs" fontWeight="bold">{token.aiScore}</CircularProgressLabel>
                        </CircularProgress>
                        <Text 
                          fontSize="sm" 
                          fontWeight="medium"
                          color={token.change24h.startsWith('+') ? 'green.500' : 'red.500'}
                          mt={1}
                        >
                          {token.change24h}
                        </Text>
                      </Flex>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          </Box>
        </VStack>
        
        <VStack align="stretch" spacing={4}>
          {selectedToken ? (
            <>
              <Box 
                bg={cardBg} 
                p={4} 
                borderRadius="lg" 
                borderWidth="1px" 
                borderColor={borderColor}
              >
                <Heading size="md" mb={3} color={textColor}>
                  <Flex align="center" justify="space-between">
                    <Flex align="center">
                      <Icon as={FiBarChart2} mr={2} />
                      AI Analysis: {selectedToken.symbol}
                    </Flex>
                    <Badge colorScheme="brand" fontSize="sm" borderRadius="full" px={2}>
                      {selectedToken.price}
                    </Badge>
                  </Flex>
                </Heading>
                
                {isAnalyzing ? (
                  <Flex justify="center" align="center" height="300px" direction="column">
                    <Spinner size="xl" color="brand.400" thickness="4px" speed="0.65s" mb={4} />
                    <Text color={secondaryTextColor}>Analyzing {selectedToken.symbol}...</Text>
                  </Flex>
                ) : analysis ? (
                  <VStack spacing={4} align="stretch">
                    <Flex 
                      p={3} 
                      bg={analysis.recommendation.action === 'BUY' ? 'green.50' : 'red.50'} 
                      color={analysis.recommendation.action === 'BUY' ? 'green.700' : 'red.700'}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={analysis.recommendation.action === 'BUY' ? 'green.200' : 'red.200'}
                      align="center"
                      justify="space-between"
                      _dark={{
                        bg: analysis.recommendation.action === 'BUY' ? 'rgba(56, 161, 105, 0.15)' : 'rgba(229, 62, 62, 0.15)',
                        color: analysis.recommendation.action === 'BUY' ? 'green.300' : 'red.300',
                        borderColor: analysis.recommendation.action === 'BUY' ? 'green.500' : 'red.500',
                      }}
                    >
                      <HStack>
                        <Icon 
                          as={analysis.recommendation.action === 'BUY' ? FiArrowUp : FiArrowDown} 
                          boxSize={6} 
                        />
                        <Box>
                          <Heading size="md">{analysis.recommendation.action}</Heading>
                          <Text fontSize="sm">Confidence: {analysis.recommendation.confidence}</Text>
                        </Box>
                      </HStack>
                      <Button 
                        colorScheme={analysis.recommendation.action === 'BUY' ? 'green' : 'red'} 
                        size="sm"
                        leftIcon={<FiZap />}
                        onClick={handlePromoteToGovernance}
                      >
                        Create Proposal
                      </Button>
                    </Flex>
                    
                    <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                      <Box p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="sm" color={secondaryTextColor}>Target Price</Text>
                        <Text fontWeight="bold" color="green.500">{analysis.recommendation.targetPrice}</Text>
                      </Box>
                      <Box p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="sm" color={secondaryTextColor}>Stop Loss</Text>
                        <Text fontWeight="bold" color="red.500">{analysis.recommendation.stopLoss}</Text>
                      </Box>
                      <Box p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="sm" color={secondaryTextColor}>Timeframe</Text>
                        <Text fontWeight="bold">{analysis.recommendation.timeframe}</Text>
                      </Box>
                    </SimpleGrid>
                    
                    <Box>
                      <Text fontWeight="medium" mb={1}>Sentiment Analysis</Text>
                      <Flex align="center" mb={2}>
                        <Progress 
                          value={analysis.sentiment.score} 
                          colorScheme="brand" 
                          size="sm" 
                          borderRadius="full" 
                          flex="1" 
                          mr={2}
                          bgColor="gray.100"
                          _dark={{ bgColor: 'whiteAlpha.200' }}
                        />
                        <Text fontWeight="bold" fontSize="sm">{analysis.sentiment.score}/100</Text>
                      </Flex>
                      <Flex wrap="wrap" gap={2} mt={2}>
                        {analysis.sentiment.keywords.map((keyword, index) => (
                          <Tag key={index} size="sm" colorScheme="brand" borderRadius="full">
                            {keyword}
                          </Tag>
                        ))}
                      </Flex>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Text fontWeight="medium" mb={3}>Technical Indicators</Text>
                      <SimpleGrid columns={3} spacing={4}>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Buy Signals</StatLabel>
                          <StatNumber color="green.500">{analysis.technical.buySignals}</StatNumber>
                        </Stat>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Sell Signals</StatLabel>
                          <StatNumber color="red.500">{analysis.technical.sellSignals}</StatNumber>
                        </Stat>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Prediction</StatLabel>
                          <StatNumber 
                            color={analysis.technical.prediction === 'Bullish' ? 'green.500' : 'red.500'}
                          >
                            {analysis.technical.prediction}
                          </StatNumber>
                        </Stat>
                      </SimpleGrid>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Text fontWeight="medium" mb={3}>Risk Analysis</Text>
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Risk Score</StatLabel>
                          <StatNumber>{analysis.riskAnalysis.riskScore}/100</StatNumber>
                        </Stat>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Volatility</StatLabel>
                          <StatNumber>{analysis.riskAnalysis.volatility}</StatNumber>
                        </Stat>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Liquidity Risk</StatLabel>
                          <StatNumber>{analysis.riskAnalysis.liquidityRisk}</StatNumber>
                        </Stat>
                        <Stat size="sm">
                          <StatLabel fontSize="xs">Market Cycle</StatLabel>
                          <StatNumber>{analysis.riskAnalysis.marketCycle}</StatNumber>
                        </Stat>
                      </SimpleGrid>
                    </Box>
                    
                    <Box>
                      <Text fontSize="sm" color={secondaryTextColor} mb={2}>
                        <Icon as={FiInfo} mr={1} />
                        Analysis Reasoning
                      </Text>
                      <Text fontSize="sm">{analysis.recommendation.reasoning}</Text>
                    </Box>
                  </VStack>
                ) : (
                  <Flex justify="center" align="center" height="200px" direction="column">
                    <Icon as={FiSearch} boxSize={10} color="gray.400" mb={4} />
                    <Text color={secondaryTextColor}>Select a token to analyze</Text>
                  </Flex>
                )}
              </Box>
            </>
          ) : (
            <Box 
              bg={cardBg} 
              p={6} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
              height="100%"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <Icon as={FiSearch} boxSize={12} color="gray.400" mb={4} />
              <Heading size="md" mb={2} color={textColor}>Select a Token</Heading>
              <Text color={secondaryTextColor}>
                Choose a token from the trending list to see detailed AI analysis and trading recommendations
              </Text>
            </Box>
          )}
        </VStack>
      </SimpleGrid>
    </Container>
  );
};

export default DesolAI;